
import { useMemo } from "react";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
];

// Mapping between German and English routes
const routeMapping = {
  '/about': '/about-en',
  '/about-en': '/about',
  '/terms': '/terms-en',
  '/terms-en': '/terms',
  '/privacy': '/privacy-en',
  '/privacy-en': '/privacy',
  '/contact': '/contact-en',
  '/contact-en': '/contact',
  '/changelog': '/changelog',
};

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    
    // Check if we're on a page that has language-specific routes
    const currentPath = location.pathname;
    const mappedRoute = routeMapping[currentPath as keyof typeof routeMapping];
    
    if (mappedRoute) {
      navigate(mappedRoute);
    }
  };

  // Memoize currentLanguage to prevent unnecessary recalculations
  const currentLanguage = useMemo(() => {
    return languages.find(lang => lang.code === i18n.language) || languages[0];
  }, [i18n.language]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="text-uplivion-blue hover:bg-gray-100">
          <Globe className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((language) => {
          const isActive = currentLanguage.code === language.code;
          return (
            <DropdownMenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`flex items-center gap-2 cursor-pointer ${
                isActive ? 'bg-gray-100' : ''
              }`}
            >
              <span>{language.flag}</span>
              <span>{language.name}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
